import { Button, Card, CardContent, Icon } from "@elit-hair/elit-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertCircle } from "react-icons/tb";

import { CallButton } from "@/components/ui/call-button";
import { Dialpad } from "@/pages/home/components/dial-panel/dialpad";
import { PhoneNumberInput } from "@/pages/home/components/dial-panel/phone-number-input";
import { CALL_PURPOSES } from "@/pages/performance/quality-watch/utils/quality-watch.constants";
import { useCall } from "@/providers/hooks/use-call";
import { PermissionNames, usePermissions } from "@/providers/hooks/use-permissions";
import { CallStatus } from "@/types/call.types";

const MANUAL_DIALER_CONTACTS = [
    { labelKey: "manual_dialer_dial_to_angel", phone: "+4915790439715" },
    { labelKey: "manual_dialer_dial_to_baris", phone: "+4915792367203" },
    { labelKey: "manual_dialer_dial_to_saddam", phone: "+4915792512427" },
] as const;

type DialerContentProps = {
    calledPhoneNumber: string;
    setCalledPhoneNumber: (value: string) => void;
    handleHangup: () => void;
    handleNumberClick: (num: string) => void;
    callStatus: CallStatus;
};

export const DialerContent = ({
    calledPhoneNumber,
    setCalledPhoneNumber,
    handleHangup,
    handleNumberClick,
    callStatus,
}: DialerContentProps) => {
    const { startCall, initialized } = useCall();
    const { validatePermissions } = usePermissions();
    const { t } = useTranslation();

    const call = async (number: string) => {
        try {
            await startCall(number, {
                callPurpose: CALL_PURPOSES.MANUAL,
            });
        } catch (error) {
            console.error("Failed to start call:", error);
        }
    };

    const [warningAgreed, setWarningAgreed] = useState(false);

    const handleSelectContact = (phone: string) => {
        setCalledPhoneNumber(phone);
    };

    return (
        <Card className="h-full w-full bg-transparent border-none">
            <CardContent className="flex flex-col w-full p-0 my-4 gap-2">
                {warningAgreed ? (
                    <>
                        <PhoneNumberInput
                            value={calledPhoneNumber}
                            onChange={setCalledPhoneNumber}
                            onEnterPress={() => call(calledPhoneNumber)}
                        />
                        <Dialpad onNumberClick={handleNumberClick} callStatus={callStatus} />
                        <CallButton
                            className="w-full"
                            onCall={() => {
                                if (validatePermissions(PermissionNames.MICROPHONE)) {
                                    call(calledPhoneNumber);
                                }
                            }}
                            onHangup={handleHangup}
                            disabled={!calledPhoneNumber.length || !initialized}
                            callStatus={callStatus}
                        />
                        <Card className="rounded-xl border border-border/60 bg-transparent shadow-none">
                            <CardContent className="flex flex-col gap-1 p-3">
                                {MANUAL_DIALER_CONTACTS.map((contact) => (
                                    <button
                                        key={contact.phone}
                                        type="button"
                                        className="text-xs text-muted-foreground hover:text-foreground hover:underline text-left w-fit"
                                        onClick={() => handleSelectContact(contact.phone)}
                                    >
                                        {t(contact.labelKey)}
                                    </button>
                                ))}
                            </CardContent>
                        </Card>
                    </>
                ) : (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                            <p className="text-sm text-foreground text-justify">
                                {t("manual_dialer_warning_description")}
                            </p>
                            <p className="text-sm text-muted-foreground text-justify">
                                {t("manual_dialer_warning_intention")}
                            </p>
                        </div>
                        <Button onClick={() => setWarningAgreed(true)} variant="destructive" className="w-full">
                            <Icon icon={TbAlertCircle} />
                            {t("continue_to_dialer_pad")}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
